import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { EMPTY_VALUE, formatPercentRange } from "./profileDisplay";
import { ProfileSection } from "./ProfileSection";
import { PROFILE_TABLE_SX } from "./profileTableSx";
import { WasteProfileConstituentData } from "./types";

/**
 * Header and cell typography follow the drawer's Discrepancies table: caption
 * secondary for headers, body2 for values. Borderless because the section card
 * already frames the content.
 */
const headerSx = { border: 0, py: 0.5 } as const;
const cellSx = { border: 0, py: 0.75 } as const;

/**
 * Constituents are edited inside the characterization form rather than here:
 * `upsertWasteProfile` replaces the whole list in one write, so a per-row
 * inline editor would have to resend every other row on each keystroke.
 */
export const ConstituentsSection = ({
  constituents,
  onEdit,
  isReadOnly = false,
}: {
  constituents: readonly WasteProfileConstituentData[];
  onEdit: () => void;
  isReadOnly?: boolean;
}) => (
  <ProfileSection
    title="Constituents"
    action={
      !isReadOnly ? (
        <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
          Edit constituents
        </Button>
      ) : undefined
    }
  >
    {constituents.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No constituents recorded
      </Typography>
    ) : (
      <Table size="small" sx={PROFILE_TABLE_SX}>
        <TableHead>
          <TableRow>
            {["Constituent", "CAS number", "Concentration"].map((header) => (
              <TableCell key={header} sx={headerSx}>
                <Typography variant="caption" color="text.secondary">
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {constituents.map((constituent) => (
            <TableRow key={constituent.id}>
              <TableCell sx={cellSx}>
                <Typography variant="body2">{constituent.name}</Typography>
              </TableCell>
              <TableCell sx={cellSx}>
                <Typography variant="body2">{constituent.casNumber || EMPTY_VALUE}</Typography>
              </TableCell>
              <TableCell sx={cellSx}>
                <Typography variant="body2">
                  {formatPercentRange(constituent.minPercent, constituent.maxPercent) ??
                    EMPTY_VALUE}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </ProfileSection>
);
